/**
 * 商家档案客户端 + 附近距离工具（v3.35 LBS）。
 * 档案存于服务端（RPC merchantProfiles / merchantProfileSet），前端经同源 POST /rpc 调用
 * （nginx 白名单转发 /rpc → :9200 handleRpc；/api/* 未对外转发故不走该路径）。
 * 距离计算为 haversine（WGS84）。
 */

export interface MerchantProfile {
  address: string;
  name: string;
  category: string;
  city: string;
  addr: string;
  lat?: number;
  lng?: number;
  desc: string;
  updatedAt: number;
}

interface RpcEnvelope<T> {
  ok: boolean;
  result?: T;
  error?: { code: string; message: string };
}

async function rpc<T>(method: string, params: unknown[] = []): Promise<T> {
  const r = await fetch('/rpc', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method, params }),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = (await r.json()) as RpcEnvelope<T>;
  if (!j.ok) throw new Error(j.error?.message ?? j.error?.code ?? 'E_UNKNOWN');
  return j.result as T;
}

/** 拉全量商家档案（服务端：server/data/merchants.json）。 */
export async function fetchMerchantProfiles(): Promise<MerchantProfile[]> {
  const list = await rpc<MerchantProfile[]>('merchantProfiles', []);
  return Array.isArray(list) ? list : [];
}

/** upsert 我的店铺档案。 */
export async function saveMerchantProfile(
  p: Partial<MerchantProfile> & { address: string },
): Promise<MerchantProfile> {
  return rpc<MerchantProfile>('merchantProfileSet', [p]);
}

/** haversine 距离（公里）。 */
export function haversineKm(
  lat1: number, lng1: number, lat2: number, lng2: number,
): number {
  const R = 6371;
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** 浏览器定位（Promise 化；失败返回 undefined）。 */
export function getMyLocation(): Promise<{ lat: number; lng: number } | undefined> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(undefined);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(undefined),
      { timeout: 8000, maximumAge: 60000 },
    );
  });
}
